'use client'

import { IconStar } from '@/app/components/atoms/Icons'
import {
  BazaarvoiceScriptLoader,
  useBazaarvoiceRatingObserver,
} from '@/app/components/shared/BazaarvoiceScriptLoader'
import { shouldShowRating } from '@/app/lib/ratings'

import styles from './BazaarvoiceInlineRating.module.scss'
import type { BazaarvoiceInlineRatingProps } from './types'

/**
 * `BazaarvoiceInlineRating` — estrella + puntuación de Bazaarvoice en cards
 * de listado (spec.md FR-003/FR-004/FR-007, contracts/bazaarvoice-integration.md),
 * con independencia de la categoría real del producto — las cards siempre
 * usan Bazaarvoice, nunca el sistema propio (FR-003).
 *
 * El contenedor `data-bv-show="inline_rating"` real de Bazaarvoice permanece
 * SIEMPRE oculto (`styles.hidden`): en cards no se delega el pintado visual
 * en el widget nativo — se usa solo como fuente del conteo/media
 * (`useBazaarvoiceRatingObserver`), redibujados con nuestra propia insignia
 * (icono 16px + número, Figma "rating en cards", node 5912:43249) para que
 * todas las cards del sitio compartan un único maquetado propio, con
 * independencia del CSS que traiga el widget de Bazaarvoice.
 *
 * Sin insignia propia (ni estado vacío) por debajo del umbral de
 * valoraciones del entorno — a diferencia de la PDP, aquí no hay diseño de
 * estado vacío: la card simplemente no muestra puntuación (FR-009/FR-010).
 */
export function BazaarvoiceInlineRating({ productId, threshold, scriptUrl }: BazaarvoiceInlineRatingProps) {
  const { containerRef, reviewCount, ratingAverage } = useBazaarvoiceRatingObserver()
  const showBadge = shouldShowRating(reviewCount, threshold)

  return (
    <>
      <BazaarvoiceScriptLoader scriptUrl={scriptUrl} />
      <div
        ref={containerRef}
        data-bv-show="inline_rating"
        data-bv-product-id={productId}
        data-bv-seo="false"
        className={styles.hidden}
      />
      {showBadge && ratingAverage != null && (
        <span className={styles.badge}>
          <IconStar size={16} className={styles.star} />
          <span className={styles.value}>{ratingAverage.toFixed(1)}</span>
        </span>
      )}
    </>
  )
}
